import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  Dropdown,
  Empty,
  Grid,
  Input,
  Menu,
  Message,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from '@arco-design/web-react'
import {
  IconCopy,
  IconDelete,
  IconEdit,
  IconEye,
  IconMore,
  IconPause,
  IconPlayArrow,
  IconPlus,
  IconSearch,
  IconThunderbolt,
} from '@arco-design/web-react/icon'
import { AlertTriangle, Clock, DollarSign, TrendingDown, TrendingUp } from 'lucide-react'
import {
  deleteDisabledChannelsApi,
  fixChannelCapabilitiesApi,
  testAllChannelsApi,
  testChannelApi,
  updateAllChannelsBalanceApi,
  updateChannelBalanceApi,
  useBatchDeleteChannels,
  useChannels,
  useCreateChannel,
  useDeleteChannel,
  useUpdateChannel,
} from '@/hooks/useChannels'
import {
  copyChannelApi,
  getChannelKeyApi,
  getChannelOpsOverviewApi,
  parseChannelModels,
  testChannelBalanceAlertApi,
  updateChannelStatusApi,
  batchUpdateChannelStatusApi,
} from '@/api/channels'
import ChannelBalanceAdjustModal from '@/components/channels/ChannelBalanceAdjustModal'
import ChannelBalanceAlertBatchModal from '@/components/channels/ChannelBalanceAlertBatchModal'
import ChannelBalanceLedgerDrawer from '@/components/channels/ChannelBalanceLedgerDrawer'
import StatCard from '@/components/Common/StatCard'
import { useRole } from '@/hooks/useRole'
import { useSecureVerification } from '@/hooks/useSecureVerification'
import SecureVerifyModal from '@/components/Common/SecureVerifyModal'
import BatchTagModal from '@/components/channels/BatchTagModal'
import ChannelFormModal, { type ChannelFormValues } from '@/components/channels/ChannelFormModal'
import CodexUsageModal from '@/components/channels/CodexUsageModal'
import ModelTestModal from '@/components/channels/ModelTestModal'
import {
  Pill,
  renderChannelStatus,
  renderChannelType,
  renderModelTag,
  renderTimestamp,
  renderUseTime,
} from '@/utils/render'
import { lowBalancePillVariant, microUsdToUsd, usdToMicroUsd } from '@/utils/channelBalance'
import { formatMicroUsd, formatUsd, renderQuota } from '@/utils/quota'
import { CHANNEL_TYPES, CHANNEL_STATUS, PAGE_SIZE_OPTIONS } from '@/utils/constants'
import { getFormValidateErrorMessage } from '@/utils/helpers'
import type { Channel } from '@/types'

function formatModels(models: Channel['models']) {
  return parseChannelModels(models as unknown)
}

function renderCompactModels(models: Channel['models']) {
  const arr = formatModels(models)
  if (!arr.length) return <span className="text-xs text-gray-400">未配置</span>
  const shown = arr.slice(0, 3)
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      {shown.map((model) => renderModelTag(model))}
      {arr.length > shown.length && <Pill variant="neutral">+{arr.length - shown.length}</Pill>}
    </div>
  )
}

const CHANNEL_TYPE_CODEX = 57
const BALANCE_SUPPORTED_TYPES = new Set([1, 8, 10, 12, 13, 20, 25, 40, 43])

function isMultiKeyChannel(channel: Channel) {
  return Boolean(channel.channelInfo?.is_multi_key || channel.channelInfo?.isMultiKey)
}

function canUpdateBalance(channel: Channel) {
  return BALANCE_SUPPORTED_TYPES.has(channel.type) && !isMultiKeyChannel(channel)
}

function canShowCodexUsage(channel: Channel) {
  return channel.type === CHANNEL_TYPE_CODEX && !isMultiKeyChannel(channel)
}

function hasConfiguredModels(channel: Channel) {
  return formatModels(channel.models).length > 0
}

export default function ChannelPage() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [nameKw, setNameKw] = useState('')
  const [groupKw, setGroupKw] = useState('')
  const [modelKw, setModelKw] = useState('')
  const [debouncedQ, setDebouncedQ] = useState({ keyword: '', group: '', model: '' })
  const [statusFilter, setStatusFilter] = useState<'all' | 'enabled' | 'disabled'>('all')
  const [typeKey, setTypeKey] = useState<string>('all')
  const [editing, setEditing] = useState<Channel | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [selected, setSelected] = useState<number[]>([])
  const [batchTagOpen, setBatchTagOpen] = useState(false)
  const [testModalTarget, setTestModalTarget] = useState<Channel | null>(null)
  const [codexTarget, setCodexTarget] = useState<Channel | null>(null)
  const [balanceAdjustTarget, setBalanceAdjustTarget] = useState<Channel | null>(null)
  const [balanceLedgerTarget, setBalanceLedgerTarget] = useState<Channel | null>(null)
  const [batchAlertOpen, setBatchAlertOpen] = useState(false)
  const [batchAlertMode, setBatchAlertMode] = useState<'ids' | 'tag'>('ids')
  const [batchAlertTag, setBatchAlertTag] = useState('')
  const [lowBalanceFilter, setLowBalanceFilter] = useState<'all' | 'low'>('all')
  const tableAreaRef = useRef<HTMLDivElement>(null)
  const [tableBodyScrollY, setTableBodyScrollY] = useState(440)

  const { isRoot } = useRole()
  const { runSecure, verifyVisible, verifyScope, verifyContext, handleSuccess, handleCancel } =
    useSecureVerification()

  const measureTableBody = useCallback(() => {
    const el = tableAreaRef.current
    if (!el) return
    const theadReserve = 50
    setTableBodyScrollY(Math.max(240, Math.floor(el.clientHeight - theadReserve)))
  }, [])

  useLayoutEffect(() => {
    measureTableBody()
    const el = tableAreaRef.current
    const ro = new ResizeObserver(() => measureTableBody())
    if (el) ro.observe(el)
    window.addEventListener('resize', measureTableBody)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measureTableBody)
    }
  }, [measureTableBody])

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ({
        keyword: nameKw.trim(),
        group: groupKw.trim(),
        model: modelKw.trim(),
      })
    }, 400)
    return () => window.clearTimeout(t)
  }, [nameKw, groupKw, modelKw])

  useEffect(() => {
    setPage(1)
  }, [debouncedQ, statusFilter, typeKey, lowBalanceFilter])

  const listParams = useMemo(
    () => ({
      page,
      page_size: pageSize,
      tag_mode: false,
      status: statusFilter,
      type: typeKey === 'all' ? undefined : Number(typeKey),
      keyword: debouncedQ.keyword,
      group: debouncedQ.group,
      model: debouncedQ.model,
      low_balance: lowBalanceFilter === 'low',
    }),
    [debouncedQ, lowBalanceFilter, page, pageSize, statusFilter, typeKey]
  )

  const { data, isLoading, refetch } = useChannels(listParams)
  const { data: opsOverview, isLoading: opsLoading } = useQuery({
    queryKey: ['channel-ops-overview'],
    queryFn: getChannelOpsOverviewApi,
    retry: false,
  })
  const createMut = useCreateChannel()
  const updateMut = useUpdateChannel()
  const deleteMut = useDeleteChannel()
  const batchDeleteMut = useBatchDeleteChannels()

  const allItems = useMemo(() => data?.items || [], [data?.items])
  const items = allItems

  const typeOptions = useMemo(() => {
    const counts = data?.type_counts
    return [
      { value: 'all', label: `全部类型 (${counts?.all ?? data?.total ?? 0})` },
      ...CHANNEL_TYPES.map((item) => ({
        value: String(item.value),
        label: `${item.text} (${counts?.[String(item.value)] ?? 0})`,
      })),
    ]
  }, [data?.total, data?.type_counts])

  useEffect(() => {
    setSelected((prev) => prev.filter((id) => items.some((item) => item.id === id)))
  }, [items])

  useLayoutEffect(() => {
    if (!isLoading) measureTableBody()
  }, [isLoading, items.length, measureTableBody])

  const openCreate = () => {
    setEditing(null)
    setModalOpen(true)
  }

  const openEdit = (row: Channel) => {
    setEditing(row)
    setModalOpen(true)
  }

  const initialFormValues = useMemo<Partial<ChannelFormValues> | undefined>(() => {
    if (!editing) return { type: 1, priority: 0, weight: 0, models: [] }
    return {
      name: editing.name,
      type: editing.type,
      key: editing.key ?? '',
      base_url: editing.baseUrl,
      models: parseChannelModels(editing.models as unknown),
      group: editing.group,
      priority: editing.priority,
      weight: editing.weight,
      tag: editing.tag,
      balance_source: editing.balanceSource || 'upstream',
      balance_currency: editing.balanceCurrency || 'USD',
      estimated_balance_usd:
        editing.estimatedBalanceMicroUsd != null
          ? microUsdToUsd(editing.estimatedBalanceMicroUsd)
          : undefined,
      balance_warning_threshold_usd:
        editing.balanceWarningThresholdMicroUsd != null
          ? microUsdToUsd(editing.balanceWarningThresholdMicroUsd)
          : undefined,
      balance_critical_threshold_usd:
        editing.balanceCriticalThresholdMicroUsd != null
          ? microUsdToUsd(editing.balanceCriticalThresholdMicroUsd)
          : undefined,
      balance_alert_enabled: editing.balanceAlertEnabled ?? false,
      balance_alert_webhook_url: editing.balanceAlertWebhookUrl,
    }
  }, [editing])

  const buildBalancePayload = (values: ChannelFormValues) => ({
    balance_source: values.balance_source || 'upstream',
    balance_currency: values.balance_currency || 'USD',
    estimated_balance_micro_usd:
      values.estimated_balance_usd != null
        ? usdToMicroUsd(values.estimated_balance_usd)
        : undefined,
    balance_warning_threshold_micro_usd:
      values.balance_warning_threshold_usd != null
        ? usdToMicroUsd(values.balance_warning_threshold_usd)
        : undefined,
    balance_critical_threshold_micro_usd:
      values.balance_critical_threshold_usd != null
        ? usdToMicroUsd(values.balance_critical_threshold_usd)
        : undefined,
    balance_alert_enabled: values.balance_alert_enabled ?? false,
    balance_alert_webhook_url: values.balance_alert_webhook_url?.trim() || undefined,
  })

  const handleSubmit = async (values: ChannelFormValues) => {
    try {
      const payload = {
        name: values.name.trim(),
        type: values.type,
        key: values.key,
        base_url: values.base_url,
        models: values.models || [],
        group: values.group || 'default',
        priority: values.priority,
        weight: values.weight,
        tag: values.tag,
        ...buildBalancePayload(values),
      }
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, ...payload })
        Message.success('已更新渠道')
      } else {
        await createMut.mutateAsync(payload)
        Message.success('已创建渠道')
      }
      setModalOpen(false)
    } catch (err: unknown) {
      Message.error(getFormValidateErrorMessage(err))
      throw err
    }
  }

  const handleTest = async (id: number) => {
    try {
      const res = await testChannelApi(id)
      if (res.success) Message.success(`测试成功${res.time ? ` (${res.time.toFixed(2)}s)` : ''}`)
      else Message.error(res.message || '测试失败')
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : '测试失败')
    }
  }

  const handleViewKey = async (row: Channel) => {
    try {
      // 后端要求 root + 该渠道的 step-up 凭据（scope channel.key.read）
      const res = await runSecure((proof) => getChannelKeyApi(row.id, proof), {
        scope: 'channel.key.read',
        context: { channel_id: row.id },
      })
      Modal.info({
        title: `查看密钥 · ${row.name}`,
        style: { width: 560 },
        content: (
          <code className="block break-all rounded-lg border border-console-border bg-[#F8FAFC] p-3 text-xs">
            {(res as { key: string }).key}
          </code>
        ),
      })
    } catch (err) {
      if (err instanceof Error && err.message) Message.error(err.message)
    }
  }

  const handleUpdateBalance = async (id: number) => {
    try {
      await updateChannelBalanceApi(id)
      Message.success('余额已更新')
      refetch()
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : '更新余额失败')
    }
  }

  const confirmCopy = (row: Channel) => {
    Modal.confirm({
      title: `复制渠道 · ${row.name}`,
      content: '将按后端默认规则创建副本：名称追加“_复制”，并重置余额与已用额度。',
      okText: '确认复制',
      onOk: async () => {
        try {
          await copyChannelApi(row.id)
          Message.success('渠道已复制')
          refetch()
        } catch (err: unknown) {
          Message.error(err instanceof Error ? err.message : '复制失败')
          throw err
        }
      },
    })
  }

  const handleTestAllChannels = async () => {
    try {
      await testAllChannelsApi()
      Message.success('已开始后台测试全部渠道；如果已有测试在运行，后端会返回提示')
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : '操作失败')
    }
  }

  const handleUpdateAllBalance = () => {
    Modal.confirm({
      title: '更新全部渠道余额',
      content: '后端会同步查询所有启用渠道，仅支持部分上游并会跳过多密钥渠道；渠道较多时耗时较长。',
      okText: '开始更新',
      onOk: async () => {
        try {
          await updateAllChannelsBalanceApi()
          Message.success('全部余额更新已完成')
          refetch()
        } catch (err: unknown) {
          Message.error(err instanceof Error ? err.message : '更新失败')
          throw err
        }
      },
    })
  }

  const handleFixCapabilities = async () => {
    try {
      const res = await fixChannelCapabilitiesApi()
      Message.success(`已修复 ${res.success} 个渠道，失败 ${res.fails} 个`)
      refetch()
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : '操作失败')
    }
  }

  const handleDeleteDisabledChannels = () => {
    Modal.confirm({
      title: '删除所有禁用渠道',
      content: '该操作会删除所有禁用或自动禁用状态的渠道，删除后不可恢复。',
      okButtonProps: { status: 'danger' },
      okText: '确认删除',
      onOk: async () => {
        try {
          const n = await deleteDisabledChannelsApi()
          Message.success(`已删除 ${n} 个禁用渠道`)
          refetch()
        } catch (err: unknown) {
          Message.error(err instanceof Error ? err.message : '删除失败')
          throw err
        }
      },
    })
  }

  const handleToggleChannelStatus = async (row: Channel) => {
    const nextStatus =
      row.status === CHANNEL_STATUS.ENABLED
        ? CHANNEL_STATUS.MANUALLY_DISABLED
        : CHANNEL_STATUS.ENABLED
    try {
      await updateChannelStatusApi(row.id, nextStatus)
      Message.success(nextStatus === CHANNEL_STATUS.ENABLED ? '渠道已启用' : '渠道已禁用')
      refetch()
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : '状态更新失败')
    }
  }

  const handleBatchSetStatus = async (status: number) => {
    if (!selected.length) {
      Message.warning('请先勾选渠道')
      return
    }
    try {
      await batchUpdateChannelStatusApi(selected, status)
      Message.success(status === CHANNEL_STATUS.ENABLED ? '已批量启用' : '已批量禁用')
      setSelected([])
      refetch()
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : '批量更新失败')
    }
  }

  const handleTestBalanceAlert = async (row: Channel) => {
    try {
      await testChannelBalanceAlertApi(row.id)
      Message.success('测试告警已发送')
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : '发送测试告警失败')
    }
  }

  const rowActionMenu = (row: Channel) => (
    <Menu
      onClickMenuItem={(key) => {
        if (key === 'key') handleViewKey(row)
        else if (key === 'model-test' && hasConfiguredModels(row)) setTestModalTarget(row)
        else if (key === 'balance' && canUpdateBalance(row)) handleUpdateBalance(row.id)
        else if (key === 'balance-adjust') setBalanceAdjustTarget(row)
        else if (key === 'balance-ledger') setBalanceLedgerTarget(row)
        else if (key === 'balance-alert-test') handleTestBalanceAlert(row)
        else if (key === 'copy') confirmCopy(row)
        else if (key === 'codex') setCodexTarget(row)
      }}
    >
      {isRoot && (
        <Menu.Item key="key">
          <IconEye /> 查看密钥
        </Menu.Item>
      )}
      <Menu.Item key="model-test" disabled={!hasConfiguredModels(row)}>
        <IconThunderbolt /> 模型测试
      </Menu.Item>
      <Menu.Item key="balance" disabled={!canUpdateBalance(row)}>
        {canUpdateBalance(row) ? '更新上游余额' : '该类型暂不支持余额查询'}
      </Menu.Item>
      <Menu.Item key="balance-adjust">余额调整</Menu.Item>
      <Menu.Item key="balance-ledger">余额台账</Menu.Item>
      <Menu.Item key="balance-alert-test">测试余额告警</Menu.Item>
      <Menu.Item key="copy">
        <IconCopy /> 复制渠道
      </Menu.Item>
      {canShowCodexUsage(row) && (
        <Menu.Item key="codex">
          <IconEye /> Codex 统计
        </Menu.Item>
      )}
    </Menu>
  )

  const columns = [
    {
      title: '渠道',
      dataIndex: 'name',
      width: 120,
      ellipsis: true,
      render: (_: unknown, row: Channel) => (
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-[#111827]" title={row.name}>
            {row.name || '-'}
          </div>
          <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
            <Tooltip content="数据库主键 ID，删除渠道后不会顺位重排">
              <span className="font-mono text-[11px] text-slate-400">ID {row.id}</span>
            </Tooltip>
            {row.tag && <Pill variant="warning">{row.tag}</Pill>}
          </div>
        </div>
      ),
    },
    { title: '类型', dataIndex: 'type', width: 140, render: (v: number) => renderChannelType(v) },
    { title: '状态', dataIndex: 'status', width: 100, render: renderChannelStatus },
    {
      title: '分组',
      dataIndex: 'group',
      width: 110,
      ellipsis: true,
      render: (v?: string) => <span className="text-sm text-slate-700">{v || 'default'}</span>,
    },
    { title: '支持模型', dataIndex: 'models', width: 200, render: renderCompactModels },
    {
      title: '响应',
      width: 110,
      render: (_: unknown, row: Channel) => (
        <div className="space-y-1 text-xs">
          <div>
            耗时：
            {renderUseTime(
              row.responseTime != null && row.responseTime > 0 ? row.responseTime / 1000 : undefined
            )}
          </div>
          {row.testTime ? (
            <div className="text-slate-500">测试：{renderTimestamp(row.testTime)}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: '已用配额',
      width: 110,
      render: (_: unknown, row: Channel) => (
        <Tooltip content="平台向用户销售侧累计消耗的 quota，非上游 USD 成本">
          <span className="text-xs tabular-nums">
            {row.usedQuota != null ? renderQuota(row.usedQuota) : '-'}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '上游余额 (USD)',
      width: 120,
      render: (_: unknown, row: Channel) => (
        <div className="space-y-1 text-xs">
          <div className="tabular-nums font-medium">
            {row.balance !== undefined ? formatUsd(row.balance) : '-'}
          </div>
          {row.balanceUpdatedTime ? (
            <div className="text-slate-500">{renderTimestamp(row.balanceUpdatedTime)}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: '估算余额 (USD)',
      width: 130,
      render: (_: unknown, row: Channel) => {
        const pillVariant = lowBalancePillVariant(row.lowBalanceLevel ?? 'normal')
        return (
          <div className="space-y-1 text-xs">
            <div className="tabular-nums font-medium">
              {row.estimatedBalanceMicroUsd != null
                ? formatMicroUsd(row.estimatedBalanceMicroUsd)
                : '-'}
            </div>
            {pillVariant ? (
              <Pill variant={pillVariant}>
                {row.lowBalanceLevel === 'critical' ? '严重低余额' : '低余额'}
              </Pill>
            ) : null}
          </div>
        )
      },
    },
    {
      title: '近24h毛利 (USD)',
      width: 150,
      render: (_: unknown, row: Channel) => {
        const cost = row.cost24hMicroUsd ?? 0
        const revenue = row.revenue24hMicroUsd ?? 0
        if (cost === 0 && revenue === 0) return <span className="text-xs text-slate-400">-</span>
        const profit = row.profit24hMicroUsd ?? revenue - cost
        const marginPct = revenue > 0 ? (profit / revenue) * 100 : null
        const positive = profit >= 0
        return (
          <div className="space-y-1 text-xs">
            <div
              className={`tabular-nums font-medium ${positive ? 'text-emerald-600' : 'text-rose-600'}`}
            >
              {formatMicroUsd(profit)}
              {marginPct != null && (
                <span className="ml-1 font-normal text-[var(--console-text-faint)]">
                  ({marginPct.toFixed(0)}%)
                </span>
              )}
            </div>
            <div className="tabular-nums text-[var(--console-text-faint)]">
              收 {formatMicroUsd(revenue)} / 支 {formatMicroUsd(cost)}
            </div>
            {row.cost24hEstimated && <Pill variant="warning">成本为估算</Pill>}
          </div>
        )
      },
    },
    {
      title: '预计可用天数',
      width: 120,
      render: (_: unknown, row: Channel) => {
        if (row.estimatedDaysRemaining == null) {
          return <span className="text-xs text-slate-400">-</span>
        }
        const days = row.estimatedDaysRemaining
        const lowDays = days < 7
        return (
          <div className="space-y-1 text-xs">
            <div className="tabular-nums font-medium">{days.toFixed(1)} 天</div>
            {lowDays || row.lowBalanceLevel === 'warning' || row.lowBalanceLevel === 'critical' ? (
              <Pill variant={row.lowBalanceLevel === 'critical' ? 'danger' : 'warning'}>
                {days < 3 ? '即将耗尽' : '消耗偏快'}
              </Pill>
            ) : null}
          </div>
        )
      },
    },
    {
      title: '调度',
      width: 90,
      render: (_: unknown, row: Channel) => (
        <div className="space-y-1 text-xs text-slate-600">
          <div>优先级 {row.priority ?? 0}</div>
          <div>权重 {row.weight ?? 0}</div>
        </div>
      ),
    },
    { title: '创建时间', dataIndex: 'createdTime', width: 160, render: renderTimestamp },
    {
      title: '操作',
      fixed: 'right' as const,
      width: 168,
      render: (_: unknown, row: Channel) => (
        <div className="flex flex-nowrap items-center gap-0.5 whitespace-nowrap">
          {row.status === CHANNEL_STATUS.ENABLED ? (
            <Popconfirm
              title={`确定禁用渠道「${row.name}」？`}
              onOk={() => handleToggleChannelStatus(row)}
            >
              <Tooltip content="禁用渠道">
                <Button size="small" type="text" aria-label="禁用渠道" icon={<IconPause />} />
              </Tooltip>
            </Popconfirm>
          ) : (
            <Tooltip content="启用渠道">
              <Button
                size="small"
                type="text"
                aria-label="启用渠道"
                icon={<IconPlayArrow />}
                onClick={() => handleToggleChannelStatus(row)}
              />
            </Tooltip>
          )}
          <Tooltip content={hasConfiguredModels(row) ? '测试渠道' : '请先配置支持模型'}>
            <Button
              size="small"
              type="text"
              aria-label="测试渠道"
              icon={<IconThunderbolt />}
              disabled={!hasConfiguredModels(row)}
              onClick={() => handleTest(row.id)}
            />
          </Tooltip>
          <Tooltip content="编辑渠道">
            <Button
              size="small"
              type="text"
              aria-label="编辑渠道"
              icon={<IconEdit />}
              onClick={() => openEdit(row)}
            />
          </Tooltip>
          <Dropdown droplist={rowActionMenu(row)} trigger="click">
            <Button size="small" type="text" aria-label="更多操作" icon={<IconMore />} />
          </Dropdown>
          <Popconfirm
            title="确定删除该渠道？"
            onOk={() =>
              deleteMut
                .mutateAsync(row.id)
                .then(() => Message.success('已删除'))
                .catch((e: Error) => Message.error(e.message || '删除失败'))
            }
          >
            <Tooltip content="删除渠道">
              <Button
                size="small"
                type="text"
                status="danger"
                aria-label="删除渠道"
                icon={<IconDelete />}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <Card className="!rounded-2xl !border-[#EAECEF] !shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
        <div className="mb-3">
          <Typography.Text bold>运营概览</Typography.Text>
          <Typography.Paragraph type="secondary" className="!mb-0 !text-xs">
            近 24 小时渠道成本与低余额态势；点击卡片可快速筛选或定位渠道。
          </Typography.Paragraph>
        </div>
        <Grid.Row gutter={12}>
          <Grid.Col xs={24} sm={12} lg={6}>
            <button
              type="button"
              className="block w-full text-left"
              onClick={() => setLowBalanceFilter('low')}
            >
              <StatCard
                title="低余额渠道"
                value={
                  opsOverview
                    ? `${opsOverview.lowBalanceWarningCount + opsOverview.lowBalanceCriticalCount}`
                    : '-'
                }
                loading={opsLoading}
                icon={<AlertTriangle />}
                iconBgClass="bg-[rgba(245,158,11,0.12)]"
                iconTextClass="text-amber-600"
              />
            </button>
            {opsOverview ? (
              <div className="mt-1 px-1 text-[11px] text-slate-500">
                提醒 {opsOverview.lowBalanceWarningCount} · 严重{' '}
                {opsOverview.lowBalanceCriticalCount}
              </div>
            ) : null}
          </Grid.Col>
          <Grid.Col xs={24} sm={12} lg={6}>
            <StatCard
              title="近24h总成本 (USD)"
              value={opsOverview ? formatMicroUsd(opsOverview.cost24hTotalMicroUsd) : '-'}
              loading={opsLoading}
              icon={<DollarSign />}
              iconBgClass="bg-[rgba(37,99,235,0.12)]"
              iconTextClass="text-[#2563EB]"
            />
          </Grid.Col>
          <Grid.Col xs={24} sm={12} lg={6}>
            <StatCard
              title="近24h收入 (USD)"
              value={opsOverview ? formatMicroUsd(opsOverview.revenue24hTotalMicroUsd) : '-'}
              loading={opsLoading}
              icon={<DollarSign />}
              iconBgClass="bg-[rgba(16,185,129,0.12)]"
              iconTextClass="text-emerald-600"
            />
            {opsOverview ? (
              <div className="mt-1 px-1 text-[11px] text-slate-500">
                支出 {formatMicroUsd(opsOverview.cost24hTotalMicroUsd)}
              </div>
            ) : null}
          </Grid.Col>
          <Grid.Col xs={24} sm={12} lg={6}>
            <StatCard
              title="近24h毛利 (USD)"
              value={opsOverview ? formatMicroUsd(opsOverview.profit24hTotalMicroUsd) : '-'}
              loading={opsLoading}
              icon={
                (opsOverview?.profit24hTotalMicroUsd ?? 0) >= 0 ? <TrendingUp /> : <TrendingDown />
              }
              iconBgClass="bg-[rgba(139,92,246,0.12)]"
              iconTextClass="text-violet-600"
            />
            {opsOverview ? (
              <div className="mt-1 px-1 text-[11px] text-slate-500">
                {opsOverview.revenue24hTotalMicroUsd > 0
                  ? `毛利率 ${(
                      (opsOverview.profit24hTotalMicroUsd / opsOverview.revenue24hTotalMicroUsd) *
                      100
                    ).toFixed(0)}%`
                  : '近 24h 无收入'}
                {opsOverview.cost24hEstimatedChannels > 0
                  ? ` · ${opsOverview.cost24hEstimatedChannels} 个渠道成本为估算`
                  : ''}
              </div>
            ) : null}
          </Grid.Col>
          <Grid.Col xs={24} sm={12} lg={6}>
            <StatCard
              title="Top 成本渠道"
              value={opsOverview?.topCostChannels?.[0]?.channelName || '-'}
              loading={opsLoading}
              icon={<TrendingDown />}
              iconBgClass="bg-[rgba(239,68,68,0.1)]"
              iconTextClass="text-red-600"
            />
            {opsOverview?.topCostChannels?.[0] ? (
              <button
                type="button"
                className="mt-1 px-1 text-left text-[11px] text-brand hover:underline"
                onClick={() => setNameKw(opsOverview.topCostChannels[0].channelName)}
              >
                {formatMicroUsd(opsOverview.topCostChannels[0].cost24hMicroUsd)} · 点击筛选
              </button>
            ) : null}
          </Grid.Col>
          <Grid.Col xs={24} sm={12} lg={6}>
            <StatCard
              title="即将耗尽"
              value={
                opsOverview?.depletingSoonChannels?.[0]
                  ? `${opsOverview.depletingSoonChannels[0].estimatedDaysRemaining.toFixed(1)} 天`
                  : '-'
              }
              loading={opsLoading}
              icon={<Clock />}
              iconBgClass="bg-[rgba(220,38,38,0.1)]"
              iconTextClass="text-red-700"
            />
            {opsOverview?.depletingSoonChannels?.[0] ? (
              <button
                type="button"
                className="mt-1 px-1 text-left text-[11px] text-brand hover:underline"
                onClick={() => setNameKw(opsOverview.depletingSoonChannels[0].channelName)}
              >
                {opsOverview.depletingSoonChannels[0].channelName} · 点击筛选
              </button>
            ) : null}
          </Grid.Col>
        </Grid.Row>
      </Card>

      <Card className="!rounded-2xl !border-[#EAECEF] !shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="名称关键词"
              value={nameKw}
              onChange={setNameKw}
              style={{ width: 176 }}
              prefix={<IconSearch />}
              allowClear
            />
            <Input
              placeholder="分组"
              value={groupKw}
              onChange={setGroupKw}
              style={{ width: 120 }}
              allowClear
            />
            <Input
              placeholder="模型"
              value={modelKw}
              onChange={setModelKw}
              style={{ width: 140 }}
              allowClear
            />
            <Select
              placeholder="状态"
              style={{ width: 120 }}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as 'all' | 'enabled' | 'disabled')}
              options={[
                { value: 'all', label: '全部状态' },
                { value: 'enabled', label: '仅启用' },
                { value: 'disabled', label: '仅禁用' },
              ]}
            />
            <Select
              placeholder="渠道类型"
              style={{ width: 200 }}
              value={typeKey}
              onChange={setTypeKey}
              options={typeOptions}
              showSearch
            />
            <Select
              placeholder="余额状态"
              style={{ width: 120 }}
              value={lowBalanceFilter}
              onChange={(v) => setLowBalanceFilter(v as 'all' | 'low')}
              options={[
                { value: 'all', label: '全部余额' },
                { value: 'low', label: '仅低余额' },
              ]}
            />
          </div>
          <div className="flex flex-col gap-3 border-t border-[#EAECEF] pt-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <Button size="small" icon={<IconThunderbolt />} onClick={handleTestAllChannels}>
                测试全部
              </Button>
              <Button size="small" onClick={handleUpdateAllBalance}>
                更新全部余额
              </Button>
              <Button size="small" onClick={handleFixCapabilities}>
                修复能力
              </Button>
              <Button
                size="small"
                status="danger"
                type="outline"
                onClick={handleDeleteDisabledChannels}
              >
                删除禁用渠道
              </Button>
              <span className="mx-1 hidden h-4 w-px bg-[#EAECEF] sm:inline-block" aria-hidden />
              <Button
                size="small"
                onClick={() => {
                  setBatchAlertMode('tag')
                  setBatchAlertTag('')
                  setBatchAlertOpen(true)
                }}
              >
                按标签告警
              </Button>
            </div>
            <Button type="primary" icon={<IconPlus />} onClick={openCreate}>
              新建渠道
            </Button>
          </div>
        </div>
      </Card>

      {selected.length > 0 && (
        <Card className="!rounded-2xl !border-[#EAECEF]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-[#374151]">
              已选中 <b className="text-brand">{selected.length}</b> 个渠道
            </span>
            <Space wrap size={8}>
              <Button size="small" onClick={() => setSelected([])}>
                清空选择
              </Button>
              <Button size="small" onClick={() => handleBatchSetStatus(CHANNEL_STATUS.ENABLED)}>
                批量启用
              </Button>
              <Button
                size="small"
                status="warning"
                onClick={() => handleBatchSetStatus(CHANNEL_STATUS.MANUALLY_DISABLED)}
              >
                批量禁用
              </Button>
              <Button size="small" onClick={() => setBatchTagOpen(true)}>
                批量打标签
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setBatchAlertMode('ids')
                  setBatchAlertOpen(true)
                }}
              >
                批量告警设置
              </Button>
              <Popconfirm
                title={`确认删除选中的 ${selected.length} 个渠道？`}
                onOk={async () => {
                  await batchDeleteMut.mutateAsync(selected)
                  setSelected([])
                  Message.success('已批量删除')
                }}
              >
                <Button
                  size="small"
                  status="danger"
                  icon={<IconDelete />}
                  loading={batchDeleteMut.isPending}
                >
                  批量删除
                </Button>
              </Popconfirm>
            </Space>
          </div>
        </Card>
      )}

      <Card
        className="!rounded-2xl !border-[#EAECEF] !shadow-[0_4px_20px_rgba(15,23,42,0.04)] flex h-[calc(100vh-15rem)] min-h-[300px] flex-col"
        bodyStyle={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <div ref={tableAreaRef} className="min-h-0 min-w-0 flex-1 overflow-hidden">
          <Table
            rowKey="id"
            loading={isLoading}
            columns={columns}
            data={items}
            pagination={false}
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: selected,
              onChange: (keys) => setSelected(keys as number[]),
            }}
            noDataElement={
              <div className="py-10">
                <Empty description="暂无渠道，或当前筛选条件没有匹配结果" />
              </div>
            }
            scroll={{ x: 1440, y: tableBodyScrollY }}
            borderCell={false}
            stripe={false}
            className="[&_.arco-table-container]:max-w-full [&_.arco-table-container]:overflow-x-auto [&_.arco-table-th]:!py-3 [&_.arco-table-td]:!py-3"
          />
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-2 rounded-b-2xl border-t border-[#EAECEF] bg-[#FAFBFC] px-4 py-3">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={data?.total || 0}
            showTotal={(total, range) =>
              total > 0 ? `第 ${range[0]}-${range[1]} 条，共 ${total} 条` : `共 ${total} 条`
            }
            sizeCanChange
            pageSizeChangeResetCurrent
            sizeOptions={PAGE_SIZE_OPTIONS}
            onChange={(p, ps) => {
              setPage(p)
              setPageSize(ps)
            }}
          />
        </div>
      </Card>

      <ChannelFormModal
        visible={modalOpen}
        editing={editing}
        initialValues={initialFormValues}
        isRoot={isRoot}
        submitting={createMut.isPending || updateMut.isPending}
        onCancel={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />

      <BatchTagModal
        visible={batchTagOpen}
        ids={selected}
        onCancel={() => setBatchTagOpen(false)}
        onSuccess={() => {
          setSelected([])
          refetch()
        }}
      />

      <ModelTestModal
        visible={!!testModalTarget}
        channel={testModalTarget}
        onCancel={() => setTestModalTarget(null)}
      />
      <CodexUsageModal
        visible={!!codexTarget}
        channel={codexTarget}
        onCancel={() => setCodexTarget(null)}
      />
      <ChannelBalanceAdjustModal
        visible={!!balanceAdjustTarget}
        channel={balanceAdjustTarget}
        onCancel={() => setBalanceAdjustTarget(null)}
        onSuccess={() => refetch()}
      />
      <ChannelBalanceLedgerDrawer
        visible={!!balanceLedgerTarget}
        channel={balanceLedgerTarget}
        onClose={() => setBalanceLedgerTarget(null)}
      />
      <ChannelBalanceAlertBatchModal
        visible={batchAlertOpen}
        mode={batchAlertMode}
        ids={selected}
        defaultTag={batchAlertTag}
        onCancel={() => setBatchAlertOpen(false)}
        onSuccess={() => {
          refetch()
          if (batchAlertMode === 'ids') setSelected([])
        }}
      />
      <SecureVerifyModal
        visible={verifyVisible}
        scope={verifyScope || 'channel.key.read'}
        context={verifyContext}
        onCancel={handleCancel}
        onSuccess={handleSuccess}
        title="查看密钥需要二次校验"
      />
    </div>
  )
}
